import React, { useState, useEffect } from 'react';
import {
    Stack,
    Text,
    Link,
    Icon,
    IconButton,
    Spinner,
    SpinnerSize,
    Dropdown,
    TextField,
    PrimaryButton,
    DefaultButton,
} from '@fluentui/react';
import { DRIItem, DRIItemType } from '../../../Common/models/DRIItem';
import { defaultIconForType } from './DRIToolsUtility';
import { TypeBadge } from './TypeBadge';
import { itemTypeOptions } from './DRIToolsUtility';

interface DRIItemCardProps {
    item: DRIItem;
    theme: any;
    isMobile: boolean;
    isNew: boolean;
    isDeleting: boolean;
    onSave: (item: DRIItem) => void;
    onCancel: () => void;
    onDelete: () => void;
    onDuplicate?: (item: DRIItem) => void;
}

export const DRIItemCard: React.FC<DRIItemCardProps> = ({
    item: initialItem,
    theme,
    isMobile,
    isNew,
    isDeleting,
    onSave,
    onCancel,
    onDelete,
    onDuplicate,
}) => {
    const [item, setItem] = useState<DRIItem>(initialItem);
    const [isEditing, setIsEditing] = useState<boolean>(isNew);
    const [copySuccess, setCopySuccess] = useState(false);

    useEffect(() => {
        setItem(initialItem);
    }, [initialItem]);

    const handleFieldChange = (field: keyof DRIItem, value: any) => {
        setItem((prev) => ({ ...prev, [field]: value }));
    };

    const handleCopyContent = async () => {
        if (item.content) {
            await navigator.clipboard.writeText(item.content);
            setCopySuccess(true);
            setTimeout(() => setCopySuccess(false), 2000);
        }
    };

    if (!isEditing) {
        // Read-only view
        return (
            <div
                style={{
                    padding: '12px 16px',
                    backgroundColor: theme.palette.neutralLighterAlt,
                    borderRadius: '4px',
                    borderLeft: `4px solid ${theme.palette.themePrimary}`,
                    borderTop: `1px solid ${theme.palette.neutralLight}`,
                    borderRight: `1px solid ${theme.palette.neutralLight}`,
                    borderBottom: `1px solid ${theme.palette.neutralLight}`,
                    boxShadow:
                        '0 3.2px 7.2px 0 rgba(0, 0, 0, .132), 0 .6px 1.8px 0 rgba(0, 0, 0, .108)',
                }}
            >
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                    }}
                >
                    <Icon
                        iconName={defaultIconForType(item.itemType)}
                        styles={{
                            root: {
                                fontSize: 20,
                                color: theme.palette.themePrimary,
                            },
                        }}
                    />
                    <div style={{ flex: 1 }}>
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                marginBottom: '2px',
                            }}
                        >
                            <Text
                                variant="mediumPlus"
                                styles={{
                                    root: {
                                        fontWeight: 600,
                                        color: theme.palette.neutralPrimary,
                                    },
                                }}
                            >
                                {item.title}
                            </Text>
                            <TypeBadge itemType={item.itemType} theme={theme} />
                        </div>

                        {/* Description */}
                        {item.description && (
                            <Text
                                variant="small"
                                styles={{
                                    root: {
                                        color: theme.palette.neutralSecondary,
                                        display: 'block',
                                        marginTop: '2px',
                                        fontStyle: 'italic',
                                    },
                                }}
                            >
                                {item.description}
                            </Text>
                        )}

                        {/* Type-specific content */}
                        {item.itemType === 'link' && item.url && (
                            <Link
                                href={item.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                styles={{ root: { fontSize: '13px' } }}
                            >
                                {item.url}
                            </Link>
                        )}
                        {item.itemType === 'text' && item.content && (
                            <Text
                                variant="small"
                                styles={{
                                    root: {
                                        color: theme.palette.neutralSecondary,
                                        display: 'block',
                                        marginTop: '4px',
                                        whiteSpace: 'pre-wrap',
                                    },
                                }}
                            >
                                {item.content}
                            </Text>
                        )}
                        {item.itemType === 'script' && item.content && (
                            <div
                                style={{
                                    marginTop: '8px',
                                    position: 'relative',
                                }}
                            >
                                <pre
                                    style={{
                                        backgroundColor:
                                            theme.palette.neutralLighter,
                                        border: `1px solid ${theme.palette.neutralTertiaryAlt}`,
                                        borderRadius: '4px',
                                        padding: '10px 12px',
                                        margin: 0,
                                        fontSize: '12px',
                                        fontFamily:
                                            "'Cascadia Code', 'Consolas', monospace",
                                        overflowX: 'auto',
                                        whiteSpace: 'pre-wrap',
                                        wordBreak: 'break-all',
                                    }}
                                >
                                    {item.content}
                                </pre>
                                <DefaultButton
                                    text={copySuccess ? 'Copied!' : 'Copy'}
                                    iconProps={{
                                        iconName: copySuccess
                                            ? 'CheckMark'
                                            : 'Copy',
                                    }}
                                    onClick={handleCopyContent}
                                    styles={{
                                        root: {
                                            marginTop: '6px',
                                            minWidth: 'auto',
                                        },
                                    }}
                                />
                            </div>
                        )}
                        {(item.itemType === 'kusto' ||
                            item.itemType === 'sql') &&
                            item.content && (
                                <div
                                    style={{
                                        marginTop: '8px',
                                        position: 'relative',
                                    }}
                                >
                                    <pre
                                        style={{
                                            backgroundColor:
                                                theme.palette.neutralLighter,
                                            border: `1px solid ${theme.palette.neutralTertiaryAlt}`,
                                            borderRadius: '4px',
                                            padding: '10px 12px',
                                            margin: 0,
                                            fontSize: '12px',
                                            fontFamily:
                                                "'Cascadia Code', 'Consolas', monospace",
                                            overflowX: 'auto',
                                            whiteSpace: 'pre-wrap',
                                            wordBreak: 'break-all',
                                        }}
                                    >
                                        {item.content}
                                    </pre>
                                    <DefaultButton
                                        text={copySuccess ? 'Copied!' : 'Copy'}
                                        iconProps={{
                                            iconName: copySuccess
                                                ? 'CheckMark'
                                                : 'Copy',
                                        }}
                                        onClick={handleCopyContent}
                                        styles={{
                                            root: {
                                                marginTop: '6px',
                                                minWidth: 'auto',
                                            },
                                        }}
                                    />
                                </div>
                            )}
                    </div>
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                        }}
                    >
                        {isDeleting && <Spinner size={SpinnerSize.small} />}
                        <IconButton
                            iconProps={{ iconName: 'Copy' }}
                            title="Duplicate"
                            ariaLabel="Duplicate item"
                            onClick={() => onDuplicate && onDuplicate(item)}
                        />
                        <IconButton
                            iconProps={{ iconName: 'Edit' }}
                            title="Edit"
                            ariaLabel="Edit item"
                            onClick={() => setIsEditing(true)}
                        />
                        <IconButton
                            iconProps={{ iconName: 'Delete' }}
                            title="Delete"
                            ariaLabel="Delete item"
                            onClick={onDelete}
                            disabled={isDeleting}
                        />
                    </div>
                </div>

                {/* Created / Modified metadata */}
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        gap: '16px',
                        marginTop: '6px',
                        paddingTop: '6px',
                        borderTop: `1px solid ${theme.palette.neutralLight}`,
                    }}
                >
                    {item.createdBy && (
                        <Text
                            variant="tiny"
                            styles={{
                                root: {
                                    color: theme.palette.neutralTertiary,
                                },
                            }}
                        >
                            Created by {item.createdBy}
                            {item.createdDate &&
                                ` on ${new Date(
                                    item.createdDate
                                ).toLocaleDateString()}`}
                        </Text>
                    )}
                    {item.lastUpdatedBy && (
                        <Text
                            variant="tiny"
                            styles={{
                                root: {
                                    color: theme.palette.neutralTertiary,
                                },
                            }}
                        >
                            Modified by {item.lastUpdatedBy}
                            {item.lastUpdatedDate &&
                                ` on ${new Date(
                                    item.lastUpdatedDate
                                ).toLocaleDateString()}`}
                        </Text>
                    )}
                </div>
            </div>
        );
    }

    // Edit / New form
    return (
        <div
            style={{
                padding: '15px',
                backgroundColor: theme.palette.neutralLighterAlt,
                borderRadius: '4px',
                borderLeft: `4px solid ${theme.palette.themePrimary}`,
                borderTop: `1px solid ${theme.palette.neutralLight}`,
                borderRight: `1px solid ${theme.palette.neutralLight}`,
                borderBottom: `1px solid ${theme.palette.neutralLight}`,
                boxShadow:
                    '0 3.2px 7.2px 0 rgba(0, 0, 0, .132), 0 .6px 1.8px 0 rgba(0, 0, 0, .108)',
                marginBottom: isNew ? '16px' : '0',
            }}
        >
            <Stack tokens={{ childrenGap: 12 }}>
                <div
                    style={{
                        padding: '12px',
                        backgroundColor: theme.palette.neutralLighter,
                        borderRadius: '4px',
                        border: `1px solid ${theme.palette.neutralTertiaryAlt}`,
                    }}
                >
                    <Dropdown
                        label="Type"
                        selectedKey={item.itemType}
                        options={itemTypeOptions}
                        onChange={(_, option) => {
                            if (option) {
                                const newType = option.key as DRIItemType;
                                handleFieldChange('itemType', newType);
                            }
                        }}
                    />
                </div>
                <div
                    style={{
                        padding: '12px',
                        backgroundColor: theme.palette.neutralLighter,
                        borderRadius: '4px',
                        border: `1px solid ${theme.palette.neutralTertiaryAlt}`,
                    }}
                >
                    <TextField
                        label="Title"
                        value={item.title}
                        onChange={(_, v) => handleFieldChange('title', v || '')}
                        placeholder="e.g. Azure Portal, Connection Notes, Setup Script"
                        required
                        borderless
                    />
                </div>
                <div
                    style={{
                        padding: '12px',
                        backgroundColor: theme.palette.neutralLighter,
                        borderRadius: '4px',
                        border: `1px solid ${theme.palette.neutralTertiaryAlt}`,
                    }}
                >
                    <TextField
                        label="Description"
                        value={item.description || ''}
                        onChange={(_, v) =>
                            handleFieldChange('description', v || '')
                        }
                        placeholder="Optional description for this item"
                        borderless
                    />
                </div>

                {/* Conditional fields based on item type */}
                {item.itemType === 'link' && (
                    <div
                        style={{
                            padding: '12px',
                            backgroundColor: theme.palette.neutralLighter,
                            borderRadius: '4px',
                            border: `1px solid ${theme.palette.neutralTertiaryAlt}`,
                        }}
                    >
                        <TextField
                            label="URL"
                            value={item.url || ''}
                            onChange={(_, v) =>
                                handleFieldChange('url', v || '')
                            }
                            placeholder="https://..."
                            required
                            borderless
                        />
                    </div>
                )}
                {(item.itemType === 'text' ||
                    item.itemType === 'script' ||
                    item.itemType === 'kusto' ||
                    item.itemType === 'sql') && (
                    <div
                        style={{
                            padding: '12px',
                            backgroundColor: theme.palette.neutralLighter,
                            borderRadius: '4px',
                            border: `1px solid ${theme.palette.neutralTertiaryAlt}`,
                        }}
                    >
                        <TextField
                            label={
                                item.itemType === 'script'
                                    ? 'PowerShell Script'
                                    : item.itemType === 'kusto'
                                    ? 'Kusto Query'
                                    : item.itemType === 'sql'
                                    ? 'SQL Query'
                                    : 'Content'
                            }
                            value={item.content || ''}
                            onChange={(_, v) =>
                                handleFieldChange('content', v || '')
                            }
                            placeholder={
                                item.itemType === 'script'
                                    ? 'Get-AzContext | Select-Object ...'
                                    : item.itemType === 'kusto'
                                    ? 'StormEvents | summarize count() by State'
                                    : item.itemType === 'sql'
                                    ? 'SELECT * FROM dbo.TableName WHERE ...'
                                    : 'Enter text content...'
                            }
                            required
                            multiline
                            rows={item.itemType === 'text' ? 4 : 8}
                            styles={
                                item.itemType === 'script' ||
                                item.itemType === 'kusto' ||
                                item.itemType === 'sql'
                                    ? {
                                          field: {
                                              fontFamily:
                                                  "'Cascadia Code', 'Consolas', monospace",
                                              fontSize: '12px',
                                          },
                                      }
                                    : undefined
                            }
                            borderless
                        />
                    </div>
                )}
                <Stack horizontal tokens={{ childrenGap: 8 }}>
                    <PrimaryButton
                        text="Save"
                        onClick={() => {
                            onSave(item);
                            setIsEditing(false);
                        }}
                        disabled={
                            !item.title ||
                            (item.itemType === 'link' && !item.url) ||
                            (item.itemType !== 'link' && !item.content)
                        }
                    />
                    <IconButton
                        iconProps={{ iconName: 'Cancel' }}
                        title="Cancel"
                        ariaLabel="Cancel"
                        onClick={() => {
                            if (isNew) {
                                onCancel();
                            } else {
                                setItem(initialItem);
                                setIsEditing(false);
                            }
                        }}
                    />
                </Stack>
            </Stack>
        </div>
    );
};
